import { useEffect, useRef, useState, type RefObject } from 'react';
import { clamp } from '../core/math';
import { drawBackground } from '../core/background';
import { textFieldCacheKey } from '../core/text/settings';
import { TEXT_TECHNIQUE_ID } from '../store/layer';
import { useStudio } from '../store/studioStore';
import { useTextSettings } from './useTextSettings';
import { drawFieldView, drawText } from './fieldRender';
import {
  drawComposition,
  stepRuntimes,
  syncRuntimes,
  type CompositionInputs,
  type CompositionStats,
  type RuntimeMap,
} from './composition';
import type { TextFieldResult } from './useTextField';

export interface CompositionResult {
  stats: CompositionStats | null;
  fps: number;
  /** Current preview budget; drops below 1 when the frame rate cannot be sustained. */
  quality: number;
}

const TARGET_FRAME_MS = 1000 / 60;

/**
 * Drives the canvas.
 *
 * Runs a single animation loop that redraws when something changed and keeps running
 * while any layer is animated. Preview quality adapts: if frames get expensive the
 * budget drops so interaction stays responsive, and it recovers once there is headroom
 * again. Exports always render at full quality, so this never affects output.
 */
export function useComposition(
  canvasRef: RefObject<HTMLCanvasElement>,
  fieldResult: TextFieldResult,
  /** Draw the bare text on its background — the "before" of the before/after compare. */
  bare = false,
): CompositionResult {
  const doc = useStudio((s) => s.doc);
  const font = useStudio((s) => s.font);
  const fieldView = useStudio((s) => s.fieldView);
  const sampleCount = useStudio((s) => s.sampleCount);
  const textSettings = useTextSettings();

  const runtimes = useRef<RuntimeMap>(new Map());
  const dirty = useRef(true);
  const settled = useRef(false);
  const quality = useRef(1);
  const frameTimes = useRef<number[]>([]);
  /** Latest measurement, updated every frame regardless of the report throttle. */
  const lastStats = useRef<CompositionStats | null>(null);
  const [result, setResult] = useState<CompositionResult>({ stats: null, fps: 0, quality: 1 });

  // Any change to the document or the field means the next frame must redraw.
  useEffect(() => {
    dirty.current = true;
    settled.current = false;
  }, [doc, fieldResult.field, fieldView, sampleCount, textSettings, bare]);

  useEffect(() => {
    let raf = 0;
    let lastTime = performance.now();
    let lastReport = 0;

    const loop = (): void => {
      raf = requestAnimationFrame(loop);

      const canvas = canvasRef.current;
      const field = fieldResult.field;
      if (!canvas || !field || !font) return;

      const now = performance.now();
      const dt = Math.min(0.1, (now - lastTime) / 1000);
      lastTime = now;

      const { width, height } = doc.canvas;
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        dirty.current = true;
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // The field inspector bypasses the layer stack: it is a view of the field
      // itself, not of the composition built on top of it.
      if (fieldView !== 'render') {
        if (!dirty.current) return;
        dirty.current = false;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
        ctx.clearRect(0, 0, width, height);
        ctx.fillStyle = '#0b0b0e';
        ctx.fillRect(0, 0, width, height);
        drawFieldView(ctx, field, textSettings, fieldView, sampleCount, doc.seed);
        setResult((r) => ({ ...r, stats: null }));
        return;
      }

      // Before/after: the same text on the same background, with every technique layer
      // and the whole post chain left out. Held, not toggled — the comparison is over
      // the moment you let go, so it never becomes a state you can get lost in.
      if (bare) {
        if (!dirty.current) return;
        dirty.current = false;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
        ctx.clearRect(0, 0, width, height);
        drawBackground(ctx, doc.background, width, height, doc.seed);
        const textLayer = doc.layers.find((l) => l.techniqueId === TEXT_TECHNIQUE_ID);
        const color = typeof textLayer?.values.color === 'string' ? textLayer.values.color : '#f2f2f7';
        drawText(ctx, field, textSettings, color);
        return;
      }

      const inputs: CompositionInputs = {
        field,
        fieldKey: `${doc.fontId}|${textFieldCacheKey(textSettings, width, height)}`,
        textSettings,
        layers: doc.layers,
        background: doc.background,
        postFx: doc.postFx,
        seed: doc.seed,
        width,
        height,
        quality: quality.current,
        scale: 1,
      };

      const stepStart = performance.now();
      syncRuntimes(inputs, runtimes.current);
      const moved = stepRuntimes(inputs, runtimes.current, dt);
      const stepMs = performance.now() - stepStart;

      if (!dirty.current && !moved) {
        // Settled. Publish the *latest* measurement rather than whatever the throttled
        // reporter last happened to emit — otherwise the status bar freezes on a count
        // from the middle of the build.
        if (!settled.current) {
          settled.current = true;
          quality.current = 1;
          frameTimes.current.length = 0;
          setResult({ stats: lastStats.current, fps: 0, quality: 1 });
        }
        return;
      }
      settled.current = false;
      dirty.current = false;

      const drawn = drawComposition(ctx, inputs, runtimes.current);
      const stats: CompositionStats = { ...drawn, drawMs: stepMs + drawn.drawMs, animating: false };
      lastStats.current = stats;
      // Post-processing is a fixed per-frame cost that the adaptive budget cannot
      // reduce, so it is excluded from the frame-time average that drives quality.

      // Adaptive quality reacts to the whole frame cost — simulation included. Judging
      // by the draw alone would miss the expensive half entirely.
      const times = frameTimes.current;
      times.push(stats.drawMs);
      if (times.length > 30) times.shift();
      const avg = times.reduce((a, b) => a + b, 0) / times.length;
      if (avg > TARGET_FRAME_MS * 1.6) quality.current = clamp(quality.current * 0.9, 0.2, 1);
      else if (avg < TARGET_FRAME_MS * 0.7) quality.current = clamp(quality.current * 1.05, 0.2, 1);

      if (now - lastReport > 200) {
        lastReport = now;
        setResult({ stats: { ...stats, animating: true }, fps: dt > 0 ? 1 / dt : 0, quality: quality.current });
      }
    };

    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [canvasRef, doc, font, fieldResult.field, fieldView, sampleCount, textSettings, bare]);

  return result;
}
